import { Inject, Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { ErrorCode, GenericResponseTotalPortfolioAnalyticsResponseBody, InputPortfolio, TotalPortfolioAnalyticsResponseBody, WweInputTotalPortfolio } from '@blk/wealth-web-endpoint';
import { ModelsEntitlementService } from '@ddt-web/common';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { ConfigService, InjectConfig, PeConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { AnalysisParams, ApiService, ApiSettings, ApiStoreConfiguration, AppStoreConfig, DataPortfolioAnalysisService, PortfolioAnalysisOptions, WwePortfolioAnalysisServiceParams, WweRcResolverService } from '@ddt-web/data-service';
import { ErrorObject, PerformanceDateMisalignedError, WwePortfolioAnalysisPartialError } from '@ddt-web/error-handler';
import { SecurityIdentifierUtil } from '@ddt-web/generic-utils';
import { StoreService } from '@ddt-web/store-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as compact from 'lodash/compact';
import * as fromPairs from 'lodash/fromPairs';
import * as get from 'lodash/get';
import * as keys from 'lodash/keys';
import * as min from 'lodash/min';
import * as pick from 'lodash/pick';
import * as toPairsIn from 'lodash/toPairsIn';
import * as uniq from 'lodash/uniq';
import { Observable } from 'rxjs';
import { catchError, first, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { WealthWebPortfolioAnalysisStoreMappingConfig } from '../config/portfolio-analysis-store-mapping.config';
import { WealthWebApiParamUtil } from '../utils/wealth-web-api-param.util';
import { WealthWebApiService } from './wealth-web-api.service';

@Injectable()
@UntilDestroy()
export class WealthWebDataPortfolioAnalysisService extends DataPortfolioAnalysisService {
	// RemoveStaleAnalysisForFailedPortfolios currently only set to true for PA to handle universe discrepancies between WDS and AWA
	@InjectConfig(SharedConfigKey.RemoveStaleAnalysisForFailedPortfolios) private readonly removeStaleAnalysisForFailedPortfolios: boolean = false;

	constructor(
		apiService: ApiService,
		storeService: StoreService,
		@Inject(APP_STORE_CONFIG_TOKEN) protected storeConfig: AppStoreConfig,
		contentService: ContentService,
		modelsEntitlementService: ModelsEntitlementService,
		protected wealthWebApiService: WealthWebApiService,
		protected configService: ConfigService,
		protected wweRcResolver: WweRcResolverService
	) {
		super(apiService, storeService, contentService, modelsEntitlementService);
	}

	public loadPortfolioAnalysisData(
		configs: Array<ApiStoreConfiguration>,
		generalAnalysisParams: AnalysisParams,
		performanceAnalysisParams?: AnalysisParams,
		portfolioAnalysisOptions?: PortfolioAnalysisOptions
	): void {
		this.dispatchAllDataLoaders(configs, generalAnalysisParams, performanceAnalysisParams, portfolioAnalysisOptions);
	}

	/**
	 * @description loadDefaultStoreData: performs the appropriate api calls and updates the corresponding stores.
	 * @param {apiSettings} apiSettings: api specific settings: rcs, config, and portfolioAnalysisParams.
	 * @param {AnalysisParams} analysisParams: the params used to build the api call.
	 */
	public loadDefaultStoreData(apiSettings: ApiSettings, analysisParams: AnalysisParams): void {
		const useAladdinId = this.configService.get(SharedConfigKey.AWE_USE_ALADDINID, false);
		const portfolios = WealthWebApiParamUtil.createPortfolioParams(analysisParams?.analysisParamsArray, useAladdinId);
		const benchmark = WealthWebApiParamUtil.createBenchmarkParam(analysisParams, useAladdinId);
		if (this.isValidAnalysisData(portfolios)) {
			this.wweRcResolver.getRcForUserType(apiSettings.config.name)
				.pipe(
					untilDestroyed(this),
					map((rc: string) => ({
						...apiSettings,
						wwePortfolioAnalysisParams: {
							body: {
								portfolios, benchmark, simulatedPerformanceUserInputs: analysisParams?.simulatedPerformanceUserInputs
							} as WweInputTotalPortfolio,
							portfolios: analysisParams.analysisParamsArray,
							rc
						} as WwePortfolioAnalysisServiceParams
					})),
					switchMap((settings: ApiSettings) => {
						return this.dispatchFetchAwaPortfolioAnalysisApiCall(settings).pipe(map((response => [settings, response])));
					})
				)
				.subscribe(([settings, response]: [ApiSettings, GenericResponseTotalPortfolioAnalyticsResponseBody]) => {
					this.processSuccessfulAnalysis(get(response, 'resultList', []).filter(port => port.success), settings);
					if (apiSettings?.config.name === 'performance') {
						this.checkPerformanceDate(get(response, 'resultList', []));
					}
					this.processFailedAnalysis(response, settings);
				});
		}
		else {
			this.storeService.upsertMany(apiSettings.config, []);
		}
	}

	protected isValidAnalysisData(portfolios: InputPortfolio[]): boolean {
		const tolerance = this.configService.get<number>(PeConfigKey.HOLDINGS_ERROR_WEIGHT_TOLERANCE, 0.01) / 100;
		return portfolios.every(port => {
			const total = port.positions.reduce((acc, pos) => acc + pos.weight, 0);
			return Math.abs(1 - total) < tolerance;
		});
	}

	protected dispatchFetchAwaPortfolioAnalysisApiCall(apiSettings: ApiSettings): Observable<GenericResponseTotalPortfolioAnalyticsResponseBody> {
		// Create the observable which handles critical errors
		return this.wealthWebApiService.fetchPortfolioAnalysis(apiSettings.wwePortfolioAnalysisParams).pipe(
			first(),
			catchError((error) => {
				this.storeService.upsertMany(apiSettings.config, []);
				Object.assign(error, { 'portfolioUniqueId': apiSettings?.wwePortfolioAnalysisParams?.portfolios[0]?.uniqueId });
				throw new ErrorObject(error, apiSettings.config.name);
			})
		);
	}

	// Update the store with the data if succeeded
	protected processSuccessfulAnalysis(portfoliosData: Array<TotalPortfolioAnalyticsResponseBody>, apiSettings: ApiSettings): void {
		const dataToStore = portfoliosData.map(portfolioData => {
			const objectToStore = pick(portfolioData, [...WealthWebPortfolioAnalysisStoreMappingConfig[apiSettings.config.name], 'benchmark.id', 'date']);
			Object.assign(objectToStore, pick(portfolioData['portfolio'], [...WealthWebPortfolioAnalysisStoreMappingConfig[apiSettings.config.name]], ['id']));
			objectToStore.customAnalytics = fromPairs((portfolioData?.['custom']?.['analytics'] ?? []).map(customColumn => [customColumn['name'], customColumn]));
			return objectToStore;
		});
		this.updateStoreFromResponse([apiSettings.config], dataToStore);
	}

	// If any results failed then throw a partial error
	protected processFailedAnalysis(response: GenericResponseTotalPortfolioAnalyticsResponseBody, apiSettings: ApiSettings): void {
		if (response.errorCodes) {
			const requestPortfolios = apiSettings.wwePortfolioAnalysisParams.portfolios;
			const failedPortfolios = keys(response.errorCodes)
				.map(portfolioId =>
					get(requestPortfolios.find(portfolio => portfolio.uniqueId === portfolioId), 'description'))
				.filter(portfolioName => Boolean(portfolioName));
			const errorMap = new Map();
			toPairsIn(response.errorCodes).forEach((errorCode: [string, ErrorCode]) => errorMap.set(errorCode[0], errorCode[1]));

			// RemoveStaleAnalysisForFailedPortfolios currently only set to true for PA to handle universe discrepancies between WDS and AWA
			if (this.removeStaleAnalysisForFailedPortfolios) {
				const failedPortfolioIds = Array.from(errorMap.keys());
				this.storeService.removeMany(apiSettings.config, failedPortfolioIds);
			}
			this.storeService.upsertMany(apiSettings.config, []);
			throw new ErrorObject(new WwePortfolioAnalysisPartialError(failedPortfolios, errorMap), apiSettings.config?.name);
		}
	}

	protected checkPerformanceDate(resultList: any[]): void {
		if (resultList.length > 1) {
			const dates = resultList.map(res => res.portfolio.performance?.expostMetrics?.stopDate);
			if (compact(uniq(dates)).length > 1) {
				this.storeService.selectAll(this.storeConfig.positionDetails)
					.pipe(withLatestFrom(this.storeService.selectAll(this.storeConfig.associatedPortfolios)), untilDestroyed(this))
					.subscribe(([positionDetails, associatedPortfolios]) => {
						const securitiesByPortfolio = new Map<string, any>();
						const portfolioIds: any[] = [];
						const oldDate = min(dates);
						const securityDateRanges: any[][] = resultList.map(res => res.portfolio.performance?.positionPerformanceDateRange);
						securityDateRanges.forEach((securityDateRange, idx) => {
							const securities = positionDetails.entities[resultList[idx].id]?.children;
							if (securityDateRange && securities?.length) {
								const portfolio = associatedPortfolios.entities[resultList[idx].id];
								const description = portfolio.description;
								const oldSecurities = securityDateRange.filter(security => security?.dateInterval?.stopDate === oldDate);
								const resolvedSecurities = oldSecurities.map(security =>
									securities.find(s => SecurityIdentifierUtil.buildSecurityAggregateKey(s) === security.id)
								);
								if (compact(resolvedSecurities).length) {
									portfolioIds.push(resultList[idx].id);
									securitiesByPortfolio.set(description, resolvedSecurities);
								}
							}
						});
						if (securitiesByPortfolio.size) {
							throw new PerformanceDateMisalignedError(portfolioIds, securitiesByPortfolio);
						}
					});
			}
		}
	}
}
